/**
 * Pagination Schema
 *
 * Shared pagination structure used across all list endpoints.
 * Provides consistent pagination metadata following project standards.
 */

import { z } from 'zod';

/**
 * Pagination Response Schema
 *
 * Standard pagination metadata returned by all paginated endpoints.
 * Matches the pattern used throughout the codebase.
 */
export const paginationSchema = z.object({
  page: z.number().int().min(1).describe('Current page number (1-indexed)'),
  pageSize: z
    .number()
    .int()
    .min(1)
    .max(100)
    .describe('Number of items per page'),
  totalCount: z.number().int().nonnegative().describe('Total number of items'),
  totalPages: z.number().int().nonnegative().describe('Total number of pages'),
  hasNext: z.boolean().describe('True if there is a next page'),
  hasPrevious: z.boolean().describe('True if there is a previous page'),
});

export type Pagination = z.infer<typeof paginationSchema>;

/**
 * Helper function to calculate pagination metadata
 *
 * @param page - Current page number (1-indexed)
 * @param pageSize - Number of items per page
 * @param totalCount - Total number of items
 * @returns Pagination metadata object
 */
export function calculatePagination(
  page: number,
  pageSize: number,
  totalCount: number
): Pagination {
  const totalPages = Math.ceil(totalCount / pageSize);

  return {
    page,
    pageSize,
    totalCount,
    totalPages,
    hasNext: page < totalPages,
    hasPrevious: page > 1,
  };
}
